import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Plus, X, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreateProjectPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [applications, setApplications] = useState([]);
  const [selectedContractor, setSelectedContractor] = useState('');
  const [milestones, setMilestones] = useState([
    { title: '', description: '', due_date: '', amount: '' }
  ]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [jobRes, appsRes] = await Promise.all([
          axios.get(`${API}/jobs/${jobId}`),
          axios.get(`${API}/applications/job/${jobId}`, { withCredentials: true })
        ]);
        setJob(jobRes.data);
        const acceptedApps = (appsRes.data || []).filter(a => a.status === 'accepted');
        setApplications(acceptedApps);
        if (acceptedApps.length > 0) setSelectedContractor(acceptedApps[0].contractor_id);
      } catch (err) {
        toast.error('Erreur de chargement');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [jobId]);

  const addMilestone = () => {
    setMilestones([...milestones, { title: '', description: '', due_date: '', amount: '' }]);
  };

  const removeMilestone = (index) => {
    if (milestones.length <= 1) return;
    setMilestones(milestones.filter((_, i) => i !== index));
  };

  const updateMilestone = (index, field, value) => {
    const updated = [...milestones];
    updated[index][field] = value;
    setMilestones(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedContractor) {
      toast.error('Sélectionnez un sous-traitant');
      return;
    }
    if (milestones.some(m => !m.title)) {
      toast.error('Chaque jalon doit avoir un titre');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        job_id: jobId,
        contractor_id: selectedContractor,
        milestones: milestones.map(m => ({
          title: m.title,
          description: m.description || null,
          due_date: m.due_date || null,
          amount: m.amount ? parseFloat(m.amount) : null
        }))
      };

      const res = await axios.post(`${API}/projects`, payload, { withCredentials: true });
      toast.success('Projet créé avec succès!');
      navigate(`/dashboard/projects/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de création');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8" data-testid="create-project-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-[#1E3A8A] hover:underline mb-6">
          <ArrowLeft className="w-4 h-4" /> Retour
        </Link>

        <h1 className="font-heading font-bold text-3xl text-[#111827] mb-2">
          Créer un projet
        </h1>
        <p className="text-[#4B5563] mb-8">
          Chantier: {job?.title}
        </p>

        <form onSubmit={handleSubmit}>
          {/* Contractor selection */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-heading text-base">Sous-traitant</CardTitle>
            </CardHeader>
            <CardContent>
              {applications.length > 0 ? (
                <div className="space-y-2">
                  {applications.map((app) => (
                    <label
                      key={app.contractor_id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedContractor === app.contractor_id ? 'border-[#1E3A8A] bg-[#1E3A8A]/5' : 'border-gray-200'
                      }`}
                    >
                      <input
                        type="radio"
                        name="contractor"
                        value={app.contractor_id}
                        checked={selectedContractor === app.contractor_id}
                        onChange={(e) => setSelectedContractor(e.target.value)}
                        className="text-[#1E3A8A]"
                        data-testid={`select-contractor-${app.contractor_id}`}
                      />
                      <span className="font-medium">{app.contractor_name}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-[#4B5563] text-sm">
                  Aucune candidature acceptée. Acceptez d'abord une candidature pour créer un projet.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Milestones */}
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-base">Jalons du projet</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={addMilestone} className="gap-1">
                  <Plus className="w-3 h-3" /> Ajouter
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {milestones.map((m, index) => (
                  <div key={`milestone-${index}-${m.title}`} className="p-4 border rounded-lg relative" data-testid={`milestone-form-${index}`}>
                    {milestones.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeMilestone(index)}
                        className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    <p className="text-xs text-[#6B7280] mb-3">Jalon {index + 1}</p>
                    <div className="space-y-3">
                      <div>
                        <Label>Titre *</Label>
                        <Input
                          value={m.title}
                          onChange={(e) => updateMilestone(index, 'title', e.target.value)}
                          placeholder="Ex: Phase de préparation"
                          className="input-field"
                          required
                          data-testid={`milestone-title-${index}`}
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={m.description}
                          onChange={(e) => updateMilestone(index, 'description', e.target.value)}
                          placeholder="Détails du jalon..."
                          className="input-field min-h-[60px]"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Date limite</Label>
                          <Input
                            type="date"
                            value={m.due_date}
                            onChange={(e) => updateMilestone(index, 'due_date', e.target.value)}
                            className="input-field"
                          />
                        </div>
                        <div>
                          <Label>Montant (€)</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={m.amount}
                            onChange={(e) => updateMilestone(index, 'amount', e.target.value)}
                            placeholder="0.00"
                            className="input-field"
                            data-testid={`milestone-amount-${index}`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Button
            type="submit"
            className="btn-primary w-full"
            disabled={submitting || !selectedContractor}
            data-testid="create-project-submit"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Création...</>
            ) : (
              'Créer le projet'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CreateProjectPage;
